import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Chip,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  Selection,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@nextui-org/react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectUsers, selectUsersList } from '../../store/user/userSlice';
import { selectUserRole } from '../../store/auth/authSlice';
import {
  FormEvent,
  Key,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  deleteUserThunk,
  getUserList,
  setUserThunk,
} from '../../store/user/userThunks';
import { Form } from '@nextui-org/form';
import { User } from '../../types';
import UserRoleChip from './UserRoleChip';
import { AsyncListData, useAsyncList } from '@react-stately/data';
import { FaTrash } from 'react-icons/fa6';
import Icon from '../base/Icon';
import NavBar from '../base/NavBar';
import {
  ACTOR_LABELS_GENERIC,
  ROLE_LABELS_GENERIC,
} from '../../config/generic';
import { ACTOR_LABELS_SOS, ROLE_LABELS_SOS } from '../../config/sos';
import { formatIsoToLocaleDateTime } from '../../util';

export default function UserView() {
  const dispatch = useAppDispatch();
  const tableListRef = useRef<AsyncListData<User>>();
  const users = useAppSelector(selectUsers);
  const usersList = useAppSelector(selectUsersList);
  const userRole = useAppSelector(selectUserRole);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onOpenChange: onDeleteOpenChange,
  } = useDisclosure();

  const selectedUser = useMemo(
    () => (selectedUsers.length > 0 ? users[selectedUsers[0]] : undefined),
    [selectedUsers, users]
  );

  useEffect(() => {
    if (userRole === 'admin') {
      dispatch(getUserList());
    }
  }, [userRole, dispatch]);

  useEffect(() => {
    setIsConfirmed(false);
  }, [selectedUser]);

  const handleRoleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    const newSelection: string[] = [];
    for (const key of keys.values()) {
      const userId = key.toString();
      if (selectedUsers.includes(userId)) {
        continue;
      }
      newSelection.push(userId);
    }
    setSelectedUsers(newSelection);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    const name = formData.get('name');
    const role = formData.get('role');
    const username = formData.get('username');

    const actor = formData.getAll('actor').map(String);

    if (selectedUser) {
      dispatch(
        setUserThunk({
          id: selectedUser.id,
          name: name?.toString() || undefined,
          role: role?.toString() || '',
          actor: actor.length ? actor : [],
          username: username?.toString() || '',
        })
      );
    }
  };

  const handleDelete = (userId: string) => () => {
    dispatch(deleteUserThunk(userId));
  };

  const columns: { key: string; label: string }[] = [
    { key: 'username', label: 'USERNAME' },
    { key: 'name', label: 'NAME' },
    { key: 'role', label: 'ROLE' },
    { key: 'createdAt', label: 'CREATED AT' },
    { key: 'updatedAt', label: 'UPDATED AT' },
    { key: 'last_login_date', label: 'LAST LOGIN' },
    { key: 'active', label: 'IS ACTIVE' },
  ];

  const renderCell = useCallback((user: User, columnKey: Key) => {
    const cellValue = user[columnKey as keyof User];
    switch (columnKey) {
      case 'role':
        return <UserRoleChip roleType={user.role} />;
      case 'createdAt':
      case 'updatedAt':
      case 'last_login_date':
        // @ts-ignore
        return cellValue ? formatIsoToLocaleDateTime(cellValue) : '';
      case 'active': {
        const lastLogin = user['last_login_date' as keyof User];

        if (lastLogin) {
          // @ts-ignore
          const date = new Date(lastLogin);
          const now = new Date();
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(now.getMonth() - 6);

          const isWithin6Months = date >= sixMonthsAgo && date <= now;
          return isWithin6Months ? 'Active' : 'Inactive';
        } else {
          return 'Inactive';
        }
      }
      default:
        return cellValue;
    }
  }, []);

  const tableList = useAsyncList({
    async load() {
      return {
        items: usersList,
      };
    },
    async sort({ items, sortDescriptor }) {
      return {
        items: items.sort((item1, item2) => {
          const k = sortDescriptor.column as keyof User;

          let field1 = item1[k];
          let field2 = item2[k];

          if (sortDescriptor.column.toString() === 'active') {
            const lastLogin1 = item1['last_login_date' as keyof User];
            const lastLogin2 = item2['last_login_date' as keyof User];

            if (lastLogin1) {
              // @ts-ignore
              const date = new Date(lastLogin1);
              const now = new Date();
              const sixMonthsAgo = new Date();
              sixMonthsAgo.setMonth(now.getMonth() - 6);

              const isWithin6Months = date >= sixMonthsAgo && date <= now;
              field1 = isWithin6Months ? 'Active' : 'Inactive';
            } else {
              field1 = 'Inactive';
            }

            if (lastLogin2) {
              // @ts-ignore
              const date = new Date(lastLogin2);
              const now = new Date();
              const sixMonthsAgo = new Date();
              sixMonthsAgo.setMonth(now.getMonth() - 6);

              const isWithin6Months = date >= sixMonthsAgo && date <= now;
              field2 = isWithin6Months ? 'Active' : 'Inactive';
            } else {
              field2 = 'Inactive';
            }
          }

          if (!field1) {
            return 1;
          }
          if (!field2) {
            return -1;
          }
          return (
            (field1 < field2 ? -1 : 1) *
            (sortDescriptor.direction === 'ascending' ? 1 : -1)
          );
        }),
      };
    },
  });

  useEffect(() => {
    tableListRef.current = tableList;
  }, [tableList]);

  useEffect(() => {
    tableListRef.current?.reload();
  }, [usersList]);

  const userTable = (
    <Table
      isHeaderSticky
      color="primary"
      selectionMode="single"
      selectedKeys={selectedUsers}
      onSelectionChange={handleRoleSelectionChange}
      sortDescriptor={tableList.sortDescriptor}
      onSortChange={tableList.sort}
      aria-label="Users table"
      className="relative w-2/3"
      classNames={{
        base: 'max-h-full',
      }}
    >
      <TableHeader columns={columns}>
        {(col) => (
          <TableColumn key={col.key} allowsSorting>
            {col.label}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody items={tableList.items} emptyContent="No users.">
        {(item) => (
          <TableRow key={item.id}>
            {(columnKey) => (
              <TableCell>{renderCell(item, columnKey)}</TableCell>
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  const roleItems =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? Object.entries(ROLE_LABELS_GENERIC).map((o) => ({
          key: o[0],
          label: o[1].label,
        }))
      : Object.entries(ROLE_LABELS_SOS).map((o) => ({
          key: o[0],
          label: o[1].label,
        }));

  const actorItems =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? Object.entries(ACTOR_LABELS_GENERIC).map((o) => ({
          key: o[0],
          label: o[1].label,
        }))
      : Object.entries(ACTOR_LABELS_SOS).map((o) => ({
          key: o[0],
          label: o[1].label,
        }));

  const userForm = (
    <Card className="relative w-1/3 h-full">
      {selectedUser ? (
        <>
          <CardHeader className="gap-2">
            <p className="text-lg font-semibold">
              {selectedUser.name || selectedUser.username}
            </p>
            <UserRoleChip roleType={selectedUser.role} />
          </CardHeader>
          <Divider />
          <CardBody>
            <Form key={selectedUser.id} onSubmit={handleSubmit}>
              <Input
                label="Name"
                name="name"
                type="text"
                placeholder={selectedUser.name}
                defaultValue={selectedUser.name}
              />
              <Select
                isRequired
                label="Role"
                name="role"
                placeholder="User role"
                defaultSelectedKeys={[selectedUser.role]}
                items={roleItems}
              >
                {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
              </Select>
              <Input
                isRequired
                label="Username"
                name="username"
                type="text"
                placeholder={selectedUser.username}
                defaultValue={selectedUser.username}
              />
              <Select
                label="Actor"
                name="actor"
                selectionMode="multiple"
                defaultSelectedKeys={selectedUser.actor ?? []}
                items={actorItems}
              >
                {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
              </Select>
              <Divider />
              <p className="text-sm italic">
                By submitting this form, any changes are irreversible.
              </p>
              <Checkbox
                size="sm"
                isSelected={isConfirmed}
                onValueChange={setIsConfirmed}
              >
                I understand.
              </Checkbox>
              <Divider />
              <div className="flex flex-row justify-between w-full gap-2">
                <ButtonGroup>
                  <Button
                    type="submit"
                    color="primary"
                    isDisabled={!isConfirmed}
                  >
                    Save
                  </Button>
                  <Button type="reset">Reset</Button>
                </ButtonGroup>
                <Button
                  color="danger"
                  variant="flat"
                  startContent={<Icon icon={FaTrash} />}
                  onPress={onDeleteOpen}
                >
                  Delete
                </Button>
              </div>
              <Modal isOpen={isDeleteOpen} onOpenChange={onDeleteOpenChange}>
                <ModalContent>
                  {(onClose) => (
                    <>
                      <ModalHeader>
                        Delete {selectedUser.name || selectedUser.username}?
                      </ModalHeader>
                      <ModalBody>
                        <p className="text-sm italic">
                          This action is irreversible.
                        </p>
                      </ModalBody>
                      <ModalFooter>
                        <ButtonGroup variant="flat">
                          <Button
                            color="danger"
                            onPress={handleDelete(selectedUser.id)}
                          >
                            Delete
                          </Button>
                          <Button onPress={onClose}>Cancel</Button>
                        </ButtonGroup>
                      </ModalFooter>
                    </>
                  )}
                </ModalContent>
              </Modal>
            </Form>
          </CardBody>
        </>
      ) : (
        <CardBody className="w-full h-full">
          <Chip variant="flat" className="m-auto">
            Select a user
          </Chip>
        </CardBody>
      )}
    </Card>
  );

  return (
    <>
      <NavBar page={'users'} />
      <div className="relative flex flex-row w-full h-full gap-4 user-view">
        {userTable}
        {userForm}
      </div>
    </>
  );
}
